import React, { useEffect } from 'react';
import moment from 'moment';
import validator from 'validator';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, injectIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import Spinner from '../../../util/spinner-shim';

import { useStateWithCallback } from '../../../util/CustomHooks';
import AppButton from '../../partials/Navigation/AppButton';
import { CapitalButton } from '../../partials/Navigation/Buttons';
import ConfirmDialog from '../../partials/ConfirmDialog';
import Footer from '../../partials/Footer';
import InputContactInfo from '../../partials/Inputs/InputContactInfo';
import { isNumber, SelectInput, TextInput } from '../../partials/Inputs/Inputs';
import NavigationBar from '../../partials/NavigationBar';
import RedX from '../../partials/Graphics/RedX';
import StatusSave from '../../partials/StatusSave';

import styles from '../../../../css/home.module.css';
import stylesSettings from '../../../../css/settings.module.css';

import AppStore from '../../../stores/AppStore';
import AppDispatcher from '../../../dispatchers/AppDispatcher';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiUrl } from '../../../config';

const isString = (x: string) => typeof x === 'string';

const deSerializeSendTo = (sendTo: string) => (isString(sendTo) ? sendTo.split(',') : sendTo || []);

const serializeSendTo = (sendTo: string) => (Array.isArray(sendTo) ? sendTo.toString() : sendTo || undefined);

const serializeDeviceGroup = (deviceGroup: any) => (Array.isArray(deviceGroup)
  ? JSON.stringify(deviceGroup)
  : deviceGroup || undefined);

const uniqueString = (group: { name: string; icon: any; deviceGroup: any }) => `${group.name}-${group.icon}-${group.deviceGroup}`;

/**
 * Test whether a variable is value non-empty Array.
 * @param {Array} value
 * @returns value if not empty else undefined.
 */
const notEmpty = (value: string | any[]) => (Array.isArray(value) && value.length && value) || undefined;

class RunReportRule {
  id: string;
  active: string;
  address: string;
  next_notification: string;
  rule: boolean;
  transport: {
    headers: { Authorization: string };
    settings: {
      deliveryDay: any;
      deliveryTime: any;
      deviceType: any;
      frequency: any;
      reportType: string;
    };
    query: {
      tempThresholdUnit: string;
      tempThreshold: number;
      customerID: any;
      dayOffset: any;
      deviceGroup: any;
      reportTitle: any;
      sendForce: boolean;
      sendTo: any;
      utcOffset: any;
      startDate: any;
      stopDate: any;
    };
    requestType: string;
    type: string;
    url: any;
  };
  recurrence: { schedule: any; timezone: any };
  send_once_state: boolean;
  type: string;
  created_by: any;
  created_at: string;
  updated_by: any;
  updated_at: string;

  constructor() {
    const now = moment().utc().toISOString();

    const {
      data: {
        user: { id: userID },
      },
    } = AppStore;

    this.id = 'new';
    this.active = 'waiting';
    this.address = 'false';
    this.next_notification = '0';
    this.rule = true;
    this.transport = {
      headers: {
        Authorization: 'ALTRAC_AUTHORIZATION_TOKEN',
      },
      settings: {
        deliveryDay: null,
        deliveryTime: null,
        deviceType: null,
        frequency: null,
        reportType: '',
      },
      query: {
        customerID: null,
        dayOffset: undefined,
        deviceGroup: [],
        reportTitle: null,
        sendForce: false,
        sendTo: [],
        utcOffset: null,
        startDate: {},
        stopDate: {},
        tempThresholdUnit: '',
        tempThreshold: 0,
      },
      requestType: 'GET',
      type: 'webhook',
      url: null,
    };
    this.recurrence = {
      schedule: null,
      timezone: null,
    };
    this.send_once_state = false;
    this.type = 'schedule2';
    this.created_by = userID;
    this.created_at = now;
    this.updated_by = userID;
    this.updated_at = now;
  }
}

/**
 * @class PumpRunReportRule
 * Configuration to generate Pump Run Reports
 */
class PumpRunReportRule extends RunReportRule {
  constructor(reportTitle: string) {
    super();
    this.transport.settings.reportType = 'pumpRunReport';
    this.transport.query.reportTitle = reportTitle;
    this.transport.query.startDate = {
      amount: 0,
      unit: 'day',
      offset: 1,
      operation: 'startOf',
    };
    this.transport.query.stopDate = {
      amount: 0,
      offset: 1,
      operation: 'endOf',
      unit: 'day',
    };
  }
}

/**
 * @class IrrigationReportRule
 * Configuration to generate Pump Run Reports
 */
class IrrigationReportRule extends RunReportRule {
  constructor(reportTitle: null) {
    super();
    this.transport.settings.reportType = 'irrigationRunReport';
    this.transport.query.reportTitle = reportTitle;
    this.transport.query.startDate = {
      amount: 0,
      unit: 'day',
      offset: 1,
      operation: 'startOf',
    };
    this.transport.query.stopDate = {
      amount: 0,
      offset: 1,
      operation: 'endOf',
      unit: 'day',
    };
  }
}

class Option {
  name: any;
  value: string | number;
  constructor(
    translateID: string,
    label: string,
    value: string | number,
    translate: Function,
  ) {
    this.name = translate(translateID, label);
    this.value = value;
  }
}

/**
 * @class WindMachineRunReportRule
 * Configuration to generate Daily Wind Machine Run Reports
 */
class WindMachineRunReportRule extends RunReportRule {
  constructor(reportTitle: any) {
    super();
    this.transport.settings.reportType = 'windMachineRunReport';
    this.transport.query.reportTitle = reportTitle;
    this.transport.query.dayOffset = 0;
    this.transport.query.tempThreshold = 32;
    this.transport.query.tempThresholdUnit = 'f';
  }
}

/**
 * Create a cron schedule based on settings entries
 * return cron schedule
 */
const scheduleFromSettings = (
  settings: {
    deliveryDay: any;
    deliveryTime: any;
    frequency: any;
  },
  before: boolean,
) => {
  const { deliveryDay, deliveryTime, frequency } = settings;

  /**
   * random minute for report chosen to be with the hour
   * before reporting time to avoid overloading
   * servers with many reports at the same time
   */
  const hour = before ? deliveryTime - 1 : deliveryTime;
  const minute = 5 + Math.round(Math.random() * 50);
  let dayOfMonth = '*';
  let dayOfWeek = '*';
  const month = '*';

  switch (frequency) {
    case 'daily':
      dayOfWeek = '*';
      dayOfMonth = '*';
      break;

    case 'weekly':
      dayOfWeek = String(deliveryDay - 1);
      dayOfMonth = '*';
      break;

    case 'monthly':
      dayOfMonth = '1';
      dayOfWeek = '*';
      break;

    default:
      break;
  }
  const cron = `cron|${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;

  return cron;
};

/**
 * Sets up state, AppStore.newRule from existing rule.
 * return settings
 */
const settingsFromRule = (rule: { transport: any; recurrence: any }, intl: { formatMessage: any }) => {
  if (!rule.transport.url) return false;

  const { formatMessage } = intl;
  const {
    recurrence: { schedule } = {},
    transport: { query: { deviceGroup = [], sendTo = [] } = {}, url } = {},
  } = rule;

  let {
    transport: {
      query: {
        reportTitle = formatMessage({
          id: 'report.settings.run.report',

          defaultMessage: 'Run Report',
        }),
      } = {},

      settings: {
        deliveryDay = '',
        deliveryTime = '',
        deviceType = '',
        frequency = '',
        reportType = '',
      } = {},
    } = {},
  } = rule;

  const translate = (id: string, defaultMessage: string) => formatMessage({ id, defaultMessage });

  const PUMP_REPORT_TITLE = translate(
    'report.settings.pump.run.report',
    'Pump Run Report',
  );

  const IRRIGATION_REPORT_TITLE = translate(
    'report.settings.irrigation.report',
    'Irrigation Report',
  );

  const WIND_MACHINE_REPORT_TITLE = translate(
    'report.settings.windMachine.run.report',
    'Wind Machine Run Report',
  );

  const destructureCron = (scheduleD: string) => scheduleD.split('|')[1].split(' ');

  // support for rules before PumpV2
  if (typeof deviceType === 'undefined') {
    if (url.includes('windMachineRunReport')) {
      deviceType = 'windMachine';
      reportTitle = WIND_MACHINE_REPORT_TITLE;
      reportType = 'windMachineRunReport';
    } else if (url.includes('pumpRunReport')) {
      deviceType = 'pump';
      reportType = 'pumpRunReport';
      reportTitle = PUMP_REPORT_TITLE;
    } else if (url.includes('irrigationReport')) {
      deviceType = 'irrigation';
      reportType = 'irrigationReport';
      reportTitle = IRRIGATION_REPORT_TITLE;
    }

    if (schedule.startsWith('cron|')) {
      [, deliveryTime, , , deliveryDay] = destructureCron(schedule);
      deliveryTime = Number(deliveryTime);
    }

    if (isNumber(deliveryDay)) {
      deliveryDay = Number(deliveryDay) + 1;
      frequency = 'weekly';
    } else {
      deliveryDay = undefined;
      frequency = 'daily';
    }
  }

  const addresses = deSerializeSendTo(sendTo);

  const settings = {
    addresses,
    deliveryDay,
    deliveryTime,
    deviceGroup: Array.isArray(deviceGroup) && deviceGroup.length === 0 ? '' : deviceGroup,
    deviceType,
    frequency,
    reportTitle,
    reportType,
  };

  return settings;
};

/**
 * @class CustomerReportSettingsEditPage
 * @desc GUI editor for report configurations.
 */
interface StateInterface {
  addresses: any;
  changed: any;
  confirmDeleteDialogVisible: any;
  deliveryDay: any;
  deliveryTime: any;
  deviceGroup: any;
  deviceType: any;
  deviceTypes: any;
  groups: any;
  frequency: any;
  inputValuesAreValid: any;
  reportTitle: any;
  reportType: any;
  saved: any;
  statusMessageIsVisible: any;
  rule: any;
  isLoading: boolean;
}

function CustomerReportSettingsEditPage(props, context) {
  const {
    // @ts-ignore
    auth: { logout: logoutFn } = {},
    // @ts-ignore
    route: { path } = {},
    intl: { formatMessage },
    intl,
  } = props;

  const translate = (id: string, defaultMessage: string) => formatMessage({ id, defaultMessage });

  const navigate = useNavigate()
  const { customerID, ruleID = 'new' } = useParams()

  const {
    data: {
      rules: { newRule = {} } = {},
      user: { data: { email: userEmail = '' } = {} } = {},
    } = {},
  } = AppStore;

  let rule = newRule;

  let ruleSettings = {};

  if (ruleID && ruleID !== 'new') {
    rule = AppStore.getNewRule();

    if (rule && ruleID !== rule.id) {
      // initialize state for existing rule
      const rules = AppStore.getRules();
      rule = rules.find((ruleI: { id: any }) => ruleI.id === ruleID);
    }

    if (rule) {
      ruleSettings = settingsFromRule(rule, intl);
    }
  }

  const {
    transport: {
      query: {
        deviceGroup: deviceDGroup = [],
        deviceGroupChanged: defaultDeviceGroupChanged = false,
        reportTitle: reportDTitle = formatMessage({
          id: 'reports.run.title',
          defaultMessage: 'Run Report',
        }),
        sendTo = null,
      } = {},

      settings: {
        deviceType: tempDeviceType = '',
        deliveryDay: dDay = '',
        deliveryTime: dTime = '',
        frequency: freq = '',
        reportType: rType = '',
      } = {},
    } = {},
  } = rule || {};

  const tempAddress = sendTo ? deSerializeSendTo(sendTo) : [userEmail];

  const initialState = {
    addresses: tempAddress,
    changed: defaultDeviceGroupChanged,
    confirmDeleteDialogVisible: false,
    deliveryDay: dDay,
    deliveryTime: dTime,
    deviceGroup: deviceDGroup,
    deviceType: tempDeviceType,
    deviceTypes: ['pump', 'windMachine', 'irrigation'],
    groups: [],
    frequency: freq,
    inputValuesAreValid: false,
    reportTitle: reportDTitle,
    reportType: rType,
    saved: false,
    statusMessageIsVisible: false,
    rule,
    isLoading: true,
    ...ruleSettings,
  };

  const [ruleState, setRuleState] = useStateWithCallback<StateInterface>(initialState);

  const {
    reportTitle,
    deviceType,
    frequency,
    deliveryDay,
    deliveryTime,
    groups,
    addresses,
    changed,
    rule: { transport: { query: { deviceGroup = [] } = {} } = {} } = {},
  } = ruleState;

  const { applications } = AppStore.getCustomer();

  /**
   * @function handleChange
   * @desc apply changed form values to state.
   * @param {string} field - name
   * @param {*} value
   * @param {function} callback
   */
  const handleChange = (field: string, value: any, callback: any) => {
    if (!field) return;
    const typedValue = isNumber(value) ? Number(value) : value;
    const newState = {
      ...ruleState,
      [field]: typedValue,
      statusMessageIsVisible: false,
      changed: !changed,
    };

    if (field === 'frequency') {
      if (
        (deviceType === 'pump' || deviceType === 'irrigation')
        && value === 'daily'
      ) {
        newState.deliveryTime = 5;
      }

      if (value === 'weekly') {
        newState.deliveryTime = 5;
      }

      if (value === 'monthly') {
        newState.deliveryTime = 4;
      }
    }

    const PLACEHOLDER_TITLE = translate(
      'report.settings.run.report',
      'Run Report',
    );

    const PUMP_REPORT_TITLE = translate(
      'report.settings.pump.run.report',
      'Pump Run Report',
    );

    const IRRIGATION_REPORT_TITLE = translate(
      'report.settings.irrigation.report',
      'Irrigation Report',
    );

    const VALVE_REPORT_TITLE = translate(
      'report.settings.valve.run.report',
      'Valve Run Report',
    );

    const WIND_MACHINE_REPORT_TITLE = translate(
      'report.settings.windMachine.run.report',
      'Wind Machine Run Report',
    );

    const titles = {
      placeholder: PLACEHOLDER_TITLE,
      pump: PUMP_REPORT_TITLE,
      valve: VALVE_REPORT_TITLE,
      windMachine: WIND_MACHINE_REPORT_TITLE,
      irrigation: IRRIGATION_REPORT_TITLE,
    };

    if (field === 'deviceType') {
      // replace default title with device type title
      if (Object.values(titles).find((title) => title === reportTitle)) {
        newState.reportTitle = titles[value];
      }

      if (value === 'windMachine') {
        newState.frequency = 'daily';
        newState.deliveryTime = 10;
      }

      if (value === 'irrigation' || value === 'pump') {
        newState.frequency = 'daily';
        newState.deliveryTime = 5;
      }
    }

    setRuleState(newState, callback);
  };

  /**
   * @function handleEmailChange.
   * @description assign email addresses change to state.
   * @param {Array<string>} functionAddresses
   * @param {function} callback
   */
  const handleEmailChange = (functionAddresses: any, callback: any) => {
    setRuleState(
      {
        ...ruleState,
        addresses: functionAddresses,
        rule,
        changed: !changed,
        statusMessageIsVisible: false,
      },

      callback,
    );
  };

  /**
   * @function setGroups
   * @desc Resolve and sort groups from store, merging into state via functional updater
   *       to avoid spreading stale closure values.
   */
  const setGroups = ({ freshDeviceGroup, freshDeviceType }: { freshDeviceGroup?: any; freshDeviceType?: string } = {}) => {
    const activeDeviceType = freshDeviceType !== undefined ? freshDeviceType : deviceType;
    const activeDeviceGroup = freshDeviceGroup !== undefined ? freshDeviceGroup : deviceGroup;

    if (activeDeviceType) {
      const { applications: allCustomerGroups } = AppStore.getCustomer();

      const ruleGroups
        = notEmpty(activeDeviceGroup)
        && allCustomerGroups.filter((group: { groupID: any }) => activeDeviceGroup.includes(group.groupID));

      const userGroups = AppStore.getGroups();

      const tempGroups = notEmpty(ruleGroups) || notEmpty(userGroups);

      if (Array.isArray(tempGroups)) {
        const sortedGroups = tempGroups.sort((a, b) => uniqueString(a).localeCompare(uniqueString(b)));

        setRuleState((prevState) => ({ ...prevState, groups: sortedGroups, isLoading: false }));
      }
    }
  };

  /**
   * Gets user's groups which have devices of the selected deviceType
   */
  const getGroups = ({ freshDeviceType, freshDeviceGroup }: { freshDeviceType?: string; freshDeviceGroup?: any } = {}) => {
    const activeDeviceType = freshDeviceType !== undefined ? freshDeviceType : deviceType;

    if (!!applications && !!activeDeviceType) {
      const allGroupIDs = applications

        .map((x: { groupID: any }) => x.groupID)

        .toString();

      AppDispatcher.dispatch({
        action: 'get-groups',
        params: {
          all: true,
          customerID,
          deviceType: activeDeviceType,
          groupID: allGroupIDs,
        },

        callback: () => setGroups({ freshDeviceGroup, freshDeviceType }),
      });
    }
  };

  /**
   * @function editGroups
   * @desc display groups edit page
   */
  const editGroups = () => {
    const { id: customerID } = AppStore.getCustomer();

    const newPath = `/${customerID}/settings/reports/${ruleID || 'new'}/groups?${
      deviceType === 'irrigation' ? '' : `deviceType=${deviceType}`
    }`;

    navigate(newPath);
  };

  const confirmDeleteRule = () => {
    setRuleState({ ...ruleState, confirmDeleteDialogVisible: true });
  };

  const childChange = (event: { target: { name: any; value: any } }) => {
    const {
      target: { name: field, value },
    } = event;

    if (field) handleChange(field, value, undefined);
  };

  /**
   * @function hideConfirmDeleteDialog
   * @desc hide the dialog
   */
  const hideConfirmDeleteDialog = () => {
    setRuleState({ ...ruleState, confirmDeleteDialogVisible: false });
  };

  /**
   * input validator
   * @returns true if all input data is valid.
   */
  const inputValuesAreValidPreGroup = () => {
    if (!(Array.isArray(addresses) && addresses.length)) {
      return false;
    }

    if (addresses.filter((email) => !validator.isEmail(email)).length > 0) {
      return false;
    }

    if (!frequency) {
      return false;
    }

    if (frequency === 'weekly' && !isNumber(deliveryDay)) {
      return false;
    }

    if (!isNumber(deliveryTime)) {
      return false;
    }

    if (!deviceType) {
      return false;
    }

    return !(!reportTitle || reportTitle.length === 0);

  };

  /**
   * @returns true if this report is a duplicate of another report
   */
  const isDuplicateReport = () => {
    // rules retrieved by CustomerReportSettingsPage

    const rules = AppStore.getRules();

    // does this report duplicate an existing report?

    const ruleFound = rules.find((rule: { id: number; transport: any }) => {
      const { transport: { query, settings } = {} } = rule;

      if (settings) {
        // don't test report against itself
        if (ruleID === String(rule.id)) return false;
        if (deliveryTime !== settings.deliveryTime) return false;
        if (deviceType !== settings.deviceType) return false;
        if (frequency !== settings.frequency) return false;
        if (frequency === 'weekly' && deliveryDay !== settings.deliveryDay) {
          return false;
        }
        if (reportTitle !== query.reportTitle) return false;
        const duplicateSendTo = serializeSendTo(addresses.sort());
        const testSendTo = serializeSendTo(query.sendTo.split(',').sort());
        if (duplicateSendTo !== testSendTo) return false;
        const deviceGroups = serializeDeviceGroup(deviceGroup);
        const ruleDeviceGroups = serializeDeviceGroup(query.deviceGroup);
        if (deviceGroups !== ruleDeviceGroups) return false;
        // all values matched
        return true;
      }

      return false;
    });

    return !!ruleFound;
  };

  /**
   * Build the startDate/stopDate range config for pump and irrigation reports,
   * which share the same frequency-based date logic.
   */
  const buildDateRange = (freq: string, day: number) => {
    const startDate = {
      daily:   { unit: 'days',  amount: 0,       operation: 'subtract', offset: 1 },
      weekly:  { unit: 'weeks', amount: day - 1,  operation: 'day',      offset: 1 },
      monthly: { unit: 'month', amount: 0,        operation: 'startOf',  offset: 1 },
    }[freq];

    const stopDate = freq === 'monthly'
      ? { unit: 'month', amount: 0, offset: 1, operation: 'endOf' }
      : { unit: 'days',  amount: 0, offset: 1, operation: 'subtract' };

    return { startDate, stopDate };
  };

  /**
   * Update new rule in AppStore.
   */
  const storeNewRule = (functionRule) => {
    const { id: customerID } = AppStore.getCustomer();

    const { tempConv } = context;

    let storeRule = functionRule || null;

    let dayOffset = 0;

    if (ruleID === 'new') {
      switch (deviceType) {
        case 'pump':
          storeRule = new PumpRunReportRule(
            reportTitle
              || formatMessage({
                id: 'reports.pumpRunReport.title',

                defaultMessage: 'Pump Run Report',
              }),
          );

          break;

        case 'irrigation':
          storeRule = new IrrigationReportRule(
            reportTitle
              || formatMessage({
                id: 'reports.irrigationReport.title',

                defaultMessage: 'Irrigation Report',
              }),
          );

          break;

        case 'windMachine':

        default:
          storeRule = new WindMachineRunReportRule(
            reportTitle
              || formatMessage({
                id: 'reports.windMachineRunReport.title',

                defaultMessage: 'Wind Machine Run Report',
              }),
          );

          if (typeof tempConv === 'string' && tempConv.toUpperCase() === 'F') {
            storeRule.transport.query.tempThreshold = 32;

            storeRule.transport.query.tempThresholdUnit = 'f';
          } else {
            storeRule.transport.query.tempThreshold = 0;

            storeRule.transport.query.tempThresholdUnit = 'c';
          }

          break;
      }

      storeRule.transport.query.customerID = customerID;
    } else {
      storeRule = AppStore.getRule();
    }

    // get browser's timezone setting

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (storeRule) {
      if (storeRule.transport) {
        if (!('settings' in storeRule.transport)) {
          Object.assign(storeRule.transport, { settings: {} });
        }
      }

      if (!isEmpty(storeRule)) {
        switch (deviceType) {
          case 'pump':
            {
              const { startDate, stopDate } = buildDateRange(frequency, deliveryDay);
              storeRule.transport.query.startDate = startDate;
              storeRule.transport.query.stopDate = stopDate;
              dayOffset = 0;
              storeRule.transport.url = `${getApiUrl('/stats/pumpRunReport')}/stats/pumpRunReport`;
              storeRule.transport.query.recalculate = true;
            }
            break;
          case 'irrigation':
            {
              const { startDate, stopDate } = buildDateRange(frequency, deliveryDay);
              storeRule.transport.query.startDate = startDate;
              storeRule.transport.query.stopDate = stopDate;
              dayOffset = 0;
              storeRule.transport.url = `${getApiUrl('/stats/irrigationReport')}/stats/irrigationReport`;
              storeRule.transport.query.recalculate = true;
            }
            break;
          case 'windMachine':
          default:
            storeRule.transport.query.date = {
              unit: 'hour',
              amount: 0,
              offset: 0,
              operation: 'add',
            };
            dayOffset = deliveryTime ? -1 * (24 - deliveryTime) : 0;
            storeRule.transport.url = `${getApiUrl('/stats/windMachineRunReport')}/stats/windMachineRunReport`;
            storeRule.transport.query.recalculate = false;
            break;
        }

        storeRule.transport.settings.deliveryDay = deliveryDay;

        storeRule.transport.settings.deliveryTime = deliveryTime;

        storeRule.transport.settings.deviceType = deviceType;

        storeRule.transport.settings.frequency = frequency;

        storeRule.transport.query.dayOffset = dayOffset;

        storeRule.transport.query.deviceGroup = deviceGroup;

        storeRule.transport.query.reportTitle = reportTitle;

        storeRule.transport.query.sendTo = serializeSendTo(addresses);

        storeRule.transport.query.utcOffset
          = storeRule.transport.query.utcOffset || timezone;

        const isScheduledByDayBefore
          = storeRule.transport.settings.reportType === 'pumpRunReport'
          || storeRule.transport.settings.reportType === 'irrigationReport';

        storeRule.recurrence.schedule = scheduleFromSettings(
          storeRule.transport.settings,

          isScheduledByDayBefore,
        );

        storeRule.recurrence.timezone = storeRule.transport.query.utcOffset;

        AppDispatcher.dispatch({
          action: 'store-new-rule',

          noEvent: true,

          rule: storeRule,

          callback: () => {
            setRuleState((prevState) => ({
              ...prevState,

              rule: storeRule,

              inputValuesAreValid: true,

              reportTitle: prevState.reportTitle,

              saved: false,
            }));
          },
        });
      }
    }
  };

  /**
   * reset AppStore groups Array
   */
  const unloadGroups = () => {
    AppDispatcher.dispatch({ action: 'unload-groups' });
  };

  /**
   * reset AppStore newRule object
   */
  const unloadRule = () => {
    AppDispatcher.dispatch({ action: 'unload-rule' });
  };

  /**
   * @function goBack
   * @desc return to previous panel.
   */
  const goBack = () => {
    unloadGroups();
    unloadRule();
    navigate(`/${customerID}/settings/reports`);
  };

  /**
   * @function updateRule
   * @desc call API to update rule in database.
   */
  const updateRule = () => {
    const updateRuleValue = AppStore.getNewRule();

    AppDispatcher.dispatch({
      action: 'set-rule',
      ruleID,
      rule: updateRuleValue,
      update: ruleID !== 'new',
      callback: () => setRuleState(
        (prevState) => ({ ...prevState, saved: true, changed: !prevState.changed }),
        () => goBack(),
      ),
    });
  };

  /**
   * delete rule in database.
   */
  const deleteRule = () => {
    if (!ruleID || ruleID === 'new') {
      unloadRule();
      goBack();
      return;
    }

    newRule.active = 'false';

    AppDispatcher.dispatch({
      action: 'set-rule',
      ruleID,
      rule: newRule,
      update: true,
      callback: () => {
        unloadRule();
        goBack();
      },
    });
  };

  /**
   * Check all form fields for valid values.
   * @param {func} callback
   * @return true if form is valid.
   */
  const validateForm = (callback: any) => {
    const isValid = inputValuesAreValidPreGroup();

    if (isValid) {
      storeNewRule(rule);
    }

    const call = () => {
      if (isValid && callback) return callback();

      return null;
    };

    setRuleState((prevState) => ({ ...prevState, inputValuesAreValid: isValid }), call);

    return isValid;
  };

  useEffect(() => {
    validateForm(undefined);
  }, [changed]);

  const { confirmDeleteDialogVisible, saved, statusMessageIsVisible }
    = ruleState;

  const dark = '#284047';

  const light = 'white';

  const buttonStyle = {
    backgroundColor: light,
    color: dark,
    fill: dark,
    stroke: dark,
  };

  type GroupItem = { name: string; icon: any; deviceGroup: any; checked: boolean };

  const groupsButtons = groups && groups.length
    ? groups
        .sort((a: GroupItem, b: GroupItem) => uniqueString(a).localeCompare(uniqueString(b)))
        .map((group: GroupItem) => (
          <AppButton
            icon={group.icon}
            disabled
            label={group.name || group.deviceGroup}
            key={uniqueString(group)}
            style={buttonStyle}
            checked={group.checked}
          />
        ))
    : [
        <Spinner
          key="spinner"
          name="double-bounce"
          style={{ width: '30px', marginLeft: 'auto', marginRight: 'auto' }}
        />,
      ];

  const visible = saved !== false;
  const outcome = saved !== true ? 'error' : 'success';
  const ADD_REPORT = translate('report.settings.add.report', 'Create Report');

  const UPDATE_REPORT = translate(
    'report.settings.update.report',
    'Update Report',
  );

  const name = ruleID === 'new' || !deviceType ? ADD_REPORT : UPDATE_REPORT;

  /**
   * Returns a hidden placeholder option when no value is selected yet, keeping
   * <select> inputs behaving like radio buttons (once a value is chosen it
   * cannot be cleared back to an empty state).
   */
  const placeholderOption = (value: any) =>
    value ? [] : [{ name: '', value: '', selected: true, hidden: true }];

  // when there is only 1 deviceType choose it

  const reportTypeOptions = [
    ...placeholderOption(null),
    new Option('report.irrigation', 'Irrigation', 'irrigation', translate),
    new Option('report.windMachine', 'Wind Machine', 'windMachine', translate),
    new Option('report.pump', 'Pump', 'pump', translate),
  ];

  const frequencyOptions = [
    ...placeholderOption(frequency),
    new Option('general.periodicity.daily', 'Daily', 'daily', translate),
    new Option('general.periodicity.weelky', 'Weekly', 'weekly', translate),
    new Option('general.periodicity.monthly', 'Monthly', 'monthly', translate),
  ];

  // option with zero as value will not be displaye in SelectInput

  let deliveryDayOptions;

  if (frequency === 'monthly') {
    deliveryDayOptions = [
      {
        disabled: true,
        name: translate('general.time.FirstDayOfMonth', 'First day of month'),
        selected: true,
        value: undefined,
      },
    ];
  } else {
    deliveryDayOptions = [
      ...placeholderOption(deliveryDay),
      new Option('general.day.sunday', 'Sunday', 1, translate),
      new Option('general.day.monday', 'Monday', 2, translate),
      new Option('general.day.tuesday', 'Tuesday', 3, translate),
      new Option('general.day.wednesday', 'Wednesday', 4, translate),
      new Option('general.day.thursday', 'Thursday', 5, translate),
      new Option('general.day.friday', 'Friday', 6, translate),
      new Option('general.day.saturday', 'Saturday', 7, translate),
    ];
  }

  const deliveryTimeBy5am = [
    {
      disabled: true,
      name: translate('general.time.by5am', 'by 5am'),
      selected: true,
      value: 5,
    },
  ];

  // option with zero as value will not be displayed in SelectInput

  const deliveryTimeOptions = [
    ...placeholderOption(deliveryTime),
    new Option('general.time.24', '12:00 am', 0, translate),
    new Option('general.time.1', '1:00 am', 1, translate),
    new Option('general.time.2', '2:00 am', 2, translate),
    new Option('general.time.3', '3:00 am', 3, translate),
    new Option('general.time.4', '4:00 am', 4, translate),
    new Option('general.time.5', '5:00 am', 5, translate),
    new Option('general.time.6', '6:00 am', 6, translate),
    new Option('general.time.7', '7:00 am', 7, translate),
    new Option('general.time.8', '8:00 am', 8, translate),
    new Option('general.time.9', '9:00 am', 9, translate),
    new Option('general.time.10', '10:00 am', 10, translate),
    new Option('general.time.11', '11:00 am', 11, translate),
    new Option('general.time.12', '12:00 pm', 12, translate),
    new Option('general.time.13', '1:00 pm', 13, translate),
    new Option('general.time.14', '2:00 pm', 14, translate),
    new Option('general.time.15', '3:00 pm', 15, translate),
    new Option('general.time.16', '4:00 pm', 16, translate),
    new Option('general.time.17', '5:00 pm', 17, translate),
    new Option('general.time.18', '6:00 pm', 18, translate),
    new Option('general.time.19', '7:00 pm', 19, translate),
    new Option('general.time.20', '8:00 pm', 20, translate),
    new Option('general.time.21', '9:00 pm', 21, translate),
    new Option('general.time.22', '10:00 pm', 22, translate),
    new Option('general.time.23', '11:00 pm', 23, translate),
  ];

  let deliveryTiming;

  switch (frequency) {
    case 'monthly':
    case 'weekly':
      deliveryTiming = deliveryTimeBy5am;
      break;
    case 'daily':
    default:
      deliveryTiming = deviceType === 'windMachine' ? deliveryTimeOptions : deliveryTimeBy5am;
      break;
  }

  const {
    pages: { CustomerReportSettingsEditPage: title = name } = {},
  } = props;

  const saveButtonStyle = `${styles.saveButton} saveButton`;

  const validFormSave = inputValuesAreValidPreGroup();

  const saveButton = (
    <CapitalButton
      buttonFunction={updateRule}
      disabled={ruleState.isLoading || isDuplicateReport() || !validFormSave}
      hSize="h2"
      style={saveButtonStyle}
      text={translate('setting.save', 'SAVE')}
    />
  );

  const trashCan = (!ruleID || !deviceType)
    ? { icon: '', func: () => {} }
    : { icon: 'fa-trash', func: confirmDeleteRule };

  /**
   * Sets up state, AppStore.newRule from existing  rule.
   * @param {Rule} rule
   */

  const setRule = (ruleE: any) => {
    const setTempRule = ruleE || AppStore.getRule();
    const { transport: { url = '' } = {} } = rule;
    if (url) {
      const settings = settingsFromRule(rule, intl);

      // Extract the fresh values from settings (synchronously derived) so the
      // callback always sees the correct deviceType/deviceGroup regardless of
      // when the async dispatcher resolves.
      const { deviceType: freshDeviceType, deviceGroup: freshDeviceGroup } = settings as any;

      const callback = () => {
        storeNewRule(setTempRule);
        getGroups({ freshDeviceType, freshDeviceGroup });
      };

      setRuleState(
        (prevState) => ({
          ...prevState,
          tempRule: setTempRule,
          ...settings,
          changed: !prevState.changed,
        }),
        callback,
      );
    }
  };

  const getRule = () => {
    if (!ruleID || ruleID === 'new' || newRule.id === ruleID) {
      setRule(newRule);

      return;
    }
    AppDispatcher.dispatch({
      action: 'get-rule',

      ruleID,

      callback: () => setRule(undefined),
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    AppStore.addChangeListener(handleChange);
    getRule();
    getGroups();
    return () => {
      AppStore.removeChangeListener(handleChange);
    };
  }, []);

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
        <NavigationBar
          page={path}
          logOut={() => logoutFn(navigate)}
          backFunc={goBack}
          name={name}
          rightControl={trashCan}
        />

        <ConfirmDialog
          cancelCallback={hideConfirmDeleteDialog}
          confirmCallback={() => deleteRule()}
          confirmButtonText={translate('general.delete', 'DELETE')}
          title={translate('general.warning', 'WARNING')}
          question={translate('report.confirm.delete', 'Delete this report?')}
          value={ruleID}
          visible={confirmDeleteDialogVisible}
        />

        {statusMessageIsVisible && <StatusSave visible outcome={outcome} />}

        <div className={styles.pageWidth}>
          <div className={styles.pageContainer}>
            <section className={`${stylesSettings.inputArea}`}>
              <FormattedMessage
                id="report.title"
                defaultMessage="Title"
                tagName="h5"
              />

              <div
                className={`${stylesSettings.groupsSelect} ${stylesSettings.ruleInputWrap}`}
                style={{ position: 'relative' }}
              >
                <TextInput
                  childChange={childChange}
                  field="reportTitle"
                  required="required"
                  style={stylesSettings.groupNameInput}
                  value={reportTitle || ''}
                />

                {(!reportTitle || reportTitle.length === 0) && (
                  <RedX
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      height: '24px',
                      width: '24px',
                    }}
                  />
                )}
              </div>

              <FormattedMessage
                id="general.report.type"
                defaultMessage="Report Type"
                tagName="h5"
              />

              <div
                className={`${stylesSettings.groupsSelect} ${stylesSettings.ruleInputWrap}`}
              >
                {/* if individual groups have been selected deviceType can't be changed */}
                <SelectInput
                  childChange={childChange}
                  // disabled={deviceType && deviceGroup.length > 0}
                  field="deviceType"
                  options={reportTypeOptions}
                  style={stylesSettings.groupsSelectInput}
                  value={deviceType || ''}
                  defaultValue={undefined}
                  selected={undefined}
                  disabled={undefined}
                />
              </div>

              <div>
                <div className={stylesSettings.groupButtonWrapper}>
                  <button
                    type="button"
                    className={`${styles.userSectionEdit} ${styles.userSection}`}
                    disabled={!deviceType}
                    style={{
                      border: 'none',
                      cursor: `${changed || ruleID ? 'pointer' : 'default'}`,
                      marginLeft: '0px',
                      marginRight: '0px',
                      lineHeight: '32px',
                      fontSize: '18pt',
                    }}
                    onClick={() => editGroups()}
                  >
                    <FormattedMessage
                      id="reports.groups.selectGroups"
                      defaultMessage="Select Groups"
                    />
                  </button>
                </div>

                <div />
              </div>

              <FormattedMessage
                id="report.frequency"
                defaultMessage="Report Frequency"
                tagName="h5"
              />

              <div
                className={`${stylesSettings.groupsSelect} ${stylesSettings.ruleInputWrap}`}
              >
                <SelectInput
                  childChange={childChange}
                  disabled={deviceType === 'windMachine'}
                  field="frequency"
                  options={frequencyOptions}
                  style={stylesSettings.groupsSelectInput}
                  value={frequency || ''}
                  defaultValue={undefined}
                  selected={undefined}
                />
              </div>

              {frequency
                && (frequency === 'weekly' || frequency === 'monthly') && (
                  <div>
                    <FormattedMessage
                      id="report.delivery.day"
                      defaultMessage="Delivery Day"
                      tagName="h5"
                    />

                    <div
                      className={`${stylesSettings.groupsSelect} ${stylesSettings.ruleInputWrap}`}
                    >
                      <SelectInput
                        childChange={childChange}
                        disabled={frequency !== 'weekly'}
                        field="deliveryDay"
                        options={deliveryDayOptions}
                        style={stylesSettings.groupsSelectInput}
                        value={deliveryDay || ''}
                        defaultValue={undefined}
                        selected={undefined}
                      />
                    </div>
                  </div>
              )}

              <FormattedMessage
                id="report.delivery.time"
                defaultMessage="Delivery Time"
                tagName="h5"
              />

              <div
                className={`${stylesSettings.groupsSelect} ${stylesSettings.ruleInputWrap}`}
              >
                <SelectInput
                  childChange={childChange}
                  disabled={
                    frequency === 'weekly'
                    || frequency === 'monthly'
                    || (deviceType === 'pump' && frequency === 'daily')
                    || (deviceType === 'irrigation' && frequency === 'daily')
                  }
                  field="deliveryTime"
                  options={deliveryTiming}
                  style={stylesSettings.groupsSelectInput}
                  value={deliveryTime || ''}
                  defaultValue={undefined}
                  selected={undefined}
                />
              </div>

              <InputContactInfo
                isEmailOnly
                method="email"
                addresses={addresses}
                callback={handleEmailChange}
              />
            </section>

            <div className={styles.pageContainer}>
              <div className={styles.list}>{(!!deviceType) ? groupsButtons : null}</div>
            </div>

            <div>
              <div>
                <div className={styles.pageContainer}>
                  <StatusSave visible={visible} outcome={outcome} />
                  {saveButton}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
  );
}

export default injectIntl(CustomerReportSettingsEditPage);
